import type { Home, ResidentItem, Zone } from '@lmc/ts-common'
import {
  Badge,
  Button,
  CheckboxGroup,
  Collapse,
  Panel,
  theme
} from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import type React from 'react'
import type { Dispatch, SetStateAction } from 'react'
import styled from 'styled-components'
import { shouldDisplayDnacpr } from '../../ResidentProfileScreen/utils/dnacpr'

type CheckboxValueType = string | number | boolean

export type ResidentSideFiltersProps = {
  residents?: ResidentItem[]
  onClick?: () => void
  zonesData: Zone[]
  onZones: Dispatch<SetStateAction<string[]>>
  onStatuses: Dispatch<SetStateAction<string[]>>
  onRemarks: Dispatch<SetStateAction<string[]>>
  zones: string[]
  statuses: string[]
  remarks: string[]
  features: Home
  clearButtonTitle: string
  statusTitle: string
  remarksTitle: string
  zoneTitle: string
  onDefaultOpened?: boolean
  noZoneTitle?: string
  viewProTitle?: string
  onSubscribe?: () => void
  testId?: string
}

const StyledCollapse = styled(Collapse)`
  background-color: ${(props) => props.theme.color.neutral.color10} !important;
  border-radius: 8px;
`

const StyledExtraWrapper = styled.div`
  display: flex;
`

const StyledBadge = styled(Badge)`
  width: 24px;
  height: 24px;
  border-radius: 12px;
  background-color: red;
`

const StyledWrapper = styled.div`
  max-width: 345px;
  width: 100%;
  margin-top: 24px;
  padding-left: 24px;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    margin-bottom: 24px;
    max-width: unset;
    padding-right: 24px;
  }
`

const StyledCheckboxGroup = styled(CheckboxGroup)`
  display: flex !important;
  flex-direction: column !important;

  .ant-checkbox-wrapper {
    margin-left: 0px;
  }
`

const StyledProContainer = styled.div`
  display: flex !important;
  align-items: center;
  flex-direction: column;
  min-height: 90px;
  justify-content: space-around;
`

const ResidentSideFilters: React.FC<ResidentSideFiltersProps> = ({
  zonesData,
  onZones,
  onStatuses,
  onRemarks,
  zones,
  statuses,
  remarks,
  features,
  clearButtonTitle,
  statusTitle,
  remarksTitle,
  zoneTitle,
  residents,
  onDefaultOpened = true,
  noZoneTitle,
  viewProTitle,
  onSubscribe,
  testId
}) => {
  const onChangeStatus = (checkedValues: CheckboxValueType[]) =>
    onStatuses(checkedValues as string[])

  const onChangeZone = (checkedValues: CheckboxValueType[]) =>
    onZones(checkedValues as string[])

  const onChangeRemarks = (checkedValues: CheckboxValueType[]) =>
    onRemarks(checkedValues as string[])

  const getExtra = (
    values: string[],
    onClear: Dispatch<SetStateAction<string[]>>
  ) => {
    const hasValues = !!values?.length

    return (
      <StyledExtraWrapper>
        {hasValues && (
          <Button
            variant="text"
            onClick={(e) => {
              e.stopPropagation()
              onClear([])
            }}
          >
            {clearButtonTitle}
          </Button>
        )}
        <StyledBadge
          size="small"
          text={values?.length.toString()}
          color={
            hasValues ? theme.color.status.primary : theme.color.neutral.color8
          }
          textColor={
            !hasValues
              ? theme.color.neutral.color1
              : theme.color.neutral.color11
          }
          style={{
            height: 24,
            width: 24,
            padding: '4px 0px 0px 9.5px',
            marginLeft: 5,
            paddingRight: 5
          }}
        />
      </StyledExtraWrapper>
    )
  }

  const statusOptions = [
    {
      value: 'active',
      label: `Active (${
        residents?.filter((resident) => resident.status === 'active')?.length ||
        0
      })`
    },
    {
      value: 'inactive',
      label: `Inactive (${
        residents?.filter((resident) => resident.status === 'inactive')
          ?.length || 0
      })`
    },
    {
      value: 'hospitalised',
      label: `Hospitalised (${
        residents?.filter((resident) => resident.status === 'hospitalised')
          ?.length
      })`
    },
    {
      value: 'deceased',
      label: `Deceased (${
        residents?.filter((resident) => resident.status === 'deceased')
          ?.length || 0
      })`
    },
    {
      value: 'discharged',
      label: `Discharged (${
        residents?.filter((resident) => resident.status === 'discharged')
          ?.length
      })`
    },
    {
      value: 'dummy',
      label: `Example (${
        residents?.filter((resident) => resident.status === 'dummy')?.length ||
        0
      })`
    }
  ]

  const zonesOptions = zonesData.map((zone) => ({
    value: zone.id,
    label: `${zone.name} (${
      residents?.filter((resident) => zone?.id === resident.zone)?.length || 0
    })`
  }))

  const remarksOptions = [
    {
      value: 'allergies',
      label: `Alliergies (${
        residents?.filter((resident) => typeof resident.allergies === 'string')
          ?.length
      })`
    },
    {
      value: 'dnacpr',
      label: `DNACPR (${
        residents?.filter((resident) =>
          shouldDisplayDnacpr(resident.dnacpr, resident.dnacprStartDate)
        )?.length
      })`
    },
    {
      value: 'lps',
      label: `LPS (${residents?.filter((resident) => resident.dols)?.length})`
    }
  ]

  return (
    <StyledWrapper
      data-testid={testId ? `${testId}-filters` : 'residents-filters'}
    >
      <StyledCollapse
        defaultActiveKey={onDefaultOpened ? ['1'] : undefined}
        expandIconPosition="start"
        data-testid={
          testId ? `${testId}-filters-collapse` : 'residents-filters-collapse'
        }
      >
        <Panel
          header={statusTitle}
          key="1"
          extra={getExtra(statuses, onStatuses)}
          data-testid={
            testId ? `${testId}-status-panel` : 'residents-status-panel'
          }
        >
          <StyledCheckboxGroup
            onChange={onChangeStatus}
            options={statusOptions}
            value={statuses}
            data-testid={
              testId
                ? `${testId}-status-checkboxes`
                : 'residents-status-checkboxes'
            }
          />
        </Panel>
        <Panel
          header={zoneTitle}
          key="2"
          extra={getExtra(zones, onZones)}
          data-testid={testId ? `${testId}-zone-panel` : 'residents-zone-panel'}
        >
          {features?.features?.zones?.enabled ? (
            <StyledCheckboxGroup
              onChange={onChangeZone}
              value={zones}
              options={zonesOptions}
              data-testid={
                testId
                  ? `${testId}-zone-checkboxes`
                  : 'residents-zone-checkboxes'
              }
            />
          ) : (
            <StyledProContainer
              data-testid={
                testId ? `${testId}-pro-upgrade` : 'residents-pro-upgrade'
              }
            >
              <Text level={2}>{noZoneTitle}</Text>
              <Button
                variant="popout"
                onClick={onSubscribe}
                data-testid={
                  testId ? `${testId}-pro-button` : 'residents-pro-button'
                }
              >
                {viewProTitle}
              </Button>
            </StyledProContainer>
          )}
        </Panel>
        <Panel
          header={remarksTitle}
          key="3"
          extra={getExtra(remarks, onRemarks)}
          data-testid={
            testId ? `${testId}-remarks-panel` : 'residents-remarks-panel'
          }
        >
          <StyledCheckboxGroup
            onChange={onChangeRemarks}
            value={remarks}
            options={remarksOptions}
            data-testid={
              testId
                ? `${testId}-remarks-checkboxes`
                : 'residents-remarks-checkboxes'
            }
          />
        </Panel>
      </StyledCollapse>
    </StyledWrapper>
  )
}

export default ResidentSideFilters
